import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { debounce } from 'lodash';

interface DataItem {
  月份: string;
  SPU编码: string;
  SPU名称: string;
  推广金额: number;
}

interface KolSkuPromotionChartProps {
  data: DataItem[];
  loading?: boolean;
}

export const KolSkuPromotionChart: React.FC<KolSkuPromotionChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 处理数据
  const processData = (rawData: DataItem[]) => {
    if (!rawData || rawData.length === 0) return {
      months: [],
      promotionAmount: [],
      productInfo: null
    };

    // 按月份排序
    const sortedData = [...rawData].sort((a, b) => a.月份.localeCompare(b.月份));
    
    return {
      months: sortedData.map(item => item.月份),
      promotionAmount: sortedData.map(item => item.推广金额),
      productInfo: sortedData[0] || null
    };
  };

  // 初始化图表
  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const { months, promotionAmount, productInfo } = processData(data);

    const option: EChartsOption = {
      title: {
        text: 'SPU营销数据',
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: (params: any[]) => {
          if (!params[0] || !productInfo) return '';
          
          const param = params[0];
          const dataIndex = param.dataIndex;
          const item = data[dataIndex];
          
          return `
            <div class="font-bold mb-2">${item.月份}</div>
            <div>SPU编码：${item.SPU编码}</div>
            <div>SPU名称：${item.SPU名称}</div>
            <div>推广金额：¥${item.推广金额.toLocaleString(undefined, { 
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}</div>
          `;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
        top: 60
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            // 格式化日期显示，如 "2025-01" 显示为 "2025年1月"
            const [year, month] = value.split('-');
            return `${year}年${parseInt(month)}月`;
          }
        },
        boundaryGap: false
      },
      yAxis: {
        type: 'value',
        name: '推广金额(元)',
        axisLabel: {
          formatter: (value: number) => {
            return `¥${value.toLocaleString()}`;
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      series: [
        {
          type: 'line',
          data: promotionAmount,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#5470C6'
          },
          lineStyle: {
            width: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(84,112,198,0.3)'
              },
              {
                offset: 1,
                color: 'rgba(84,112,198,0.1)'
              }
            ])
          }
        }
      ]
    };

    chartInstance.current.setOption(option);
  };

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (data && data.length > 0) {
      initChart();
    }
  }, [data]);

  // 销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className="w-full h-[500px]" />
  );
};

export default KolSkuPromotionChart;